/**
 * CSV Export Utility
 * Provides functions to convert data to CSV format and trigger downloads
 */

/**
 * Escapes a value for CSV format
 * - Wraps in quotes if contains comma, quote, or newline
 * - Doubles any quotes inside the value
 */
function escapeCSVValue(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }
  
  const stringValue = String(value);
  
  // If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  
  return stringValue;
}

/**
 * Converts an array of objects to CSV string
 * @param data Array of objects to convert
 * @param headers Optional custom headers (defaults to object keys)
 * @returns CSV string
 */
export function convertToCSV(data: Record<string, any>[], headers?: string[]): string {
  if (!data || data.length === 0) {
    return '';
  }
  
  // Use provided headers or extract from first object
  const columnHeaders = headers || Object.keys(data[0]);
  
  // Create header row
  const headerRow = columnHeaders.map(escapeCSVValue).join(',');
  
  // Create data rows
  const dataRows = data.map(row => {
    return columnHeaders.map(header => escapeCSVValue(row[header])).join(',');
  });
  
  // Combine header and data rows
  return [headerRow, ...dataRows].join('\n');
}

/**
 * Triggers a browser download of CSV data
 * @param data Array of objects to export
 * @param filename Name of the file to download
 * @param headers Optional custom headers
 */
export function downloadCSV(
  data: Record<string, any>[],
  filename: string,
  headers?: string[]
): void {
  const csv = convertToCSV(data, headers);
  
  // Add UTF-8 BOM for Excel compatibility
  const BOM = '\uFEFF';
  const csvWithBOM = BOM + csv;
  
  // Create blob and download
  const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up
  URL.revokeObjectURL(url);
}
